/*
 * How a metric value is written down.
 *
 * Pure and dependency-free so it can be unit tested without a browser, and in
 * one place because the funnel, the tiles and the breakdown tables all print
 * the same kinds of number and must not round them differently. A rate that
 * reads 30.0% in a tile and 30% in a table looks like two measurements.
 *
 * The rule these all share: NULL is never rendered as zero. A suppressed rate,
 * a median with no rows behind it and a value on an unpopulated column are all
 * "we do not know", and "0" is a claim.
 */

export const CURRENCY_SYMBOL = { USD: "$", EUR: "€", GBP: "£", ILS: "₪" };

/** 12400 -> "$12.4K". Thousands only past 1000, so "$950" stays exact. */
export function money(value, currency = "USD") {
  if (value == null) return "—";
  const symbol = CURRENCY_SYMBOL[currency] || "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1000) return `${symbol}${Math.round(n / 100) / 10}K`;
  return `${symbol}${Math.round(n)}`;
}

/**
 * One tile value, formatted for its declared format. The `format` comes from
 * the metric layer rather than being sniffed from the value, because 0 and 0%
 * and $0 are different statements and a number alone cannot say which it is.
 */
export function metricValue(value, format) {
  if (value == null) return "—";
  switch (format) {
    case "percent":
      return `${Number(value).toFixed(1)}%`;
    case "currency":
      return money(value);
    case "days":
      return `${Math.round(Number(value) * 10) / 10}`;
    case "count":
    default:
      return Number(value).toLocaleString();
  }
}

/** The unit that sits beside a value, or "" when the value carries its own. */
export function metricUnit(format) {
  return format === "days" ? "days" : "";
}

/**
 * A delta chip's text and direction. Returns null when the metric layer
 * suppressed the percentage — the caller must then render the qualifier
 * instead, which is why this returns null rather than a dash: a dash in a delta
 * chip reads as "no change".
 */
export function delta(comparison) {
  if (!comparison || comparison.suppressed || comparison.pct == null) return null;
  const { pct, delta: change } = comparison;
  return {
    text: `${pct > 0 ? "↑" : pct < 0 ? "↓" : ""} ${Math.abs(pct).toFixed(1)}%`.trim(),
    absolute: `${change > 0 ? "+" : ""}${change}`,
    direction: pct > 0 ? "up" : pct < 0 ? "down" : "flat",
  };
}

/**
 * Which qualifier treatment a tile gets. Three, matching the committed design,
 * and each one maps to a real state rather than being chosen for looks:
 *
 *   dashed   the number covers a partial period — the dashed rule is the same
 *            hatching the partial sparkline bar uses
 *   aside    the comparison was suppressed; the sentence explains what replaced it
 *   plain    a standing fact with no period and nothing to compare against
 */
export function qualifierKind(tile) {
  if (tile.comparison && tile.comparison.suppressed) return "aside";
  if (tile.partial) return "dashed";
  return "plain";
}

/**
 * Sparkline bar heights, in px, scaled to the series maximum.
 *
 * An all-zero series returns all-minimum bars rather than dividing by zero, and
 * every bar keeps a 2px floor so a zero month is visibly a slot rather than a
 * gap — "nothing happened here" and "this month is missing" look identical
 * otherwise.
 */
export function sparkBars(series, height = 22) {
  const max = Math.max(1, ...series.map((p) => p.count));
  return series.map((p) => ({
    key: p.key,
    count: p.count,
    partial: !!p.partial,
    // Carried through so the bar can render as absent rather than as zero. Only
    // the cost-per-lead series sets it; everything else leaves it undefined.
    known: p.known,
    height: p.known === false ? 0 : Math.max(2, Math.round((p.count / max) * height)),
  }));
}

// Step sizes an axis is allowed to use, as multiples of a power of ten. Wider
// than the classic 1/2/5 ladder on purpose: everything drawn on this chart is a
// COUNT, and 1/2/5 turns a maximum of 10 into an axis topping out at 20, which
// wastes half the plot and reads as though the metric once hit 20. With 3, 4, 6
// and 8 available, a max of 10 tops out at 12 and a max of 22 at 24.
const AXIS_STEPS = [1, 2, 3, 4, 5, 6, 8, 10];

/**
 * A real y-axis: a rounded top and evenly divisible ticks, top tick first.
 *
 * The drill-down is the one chart on this page with an axis, and an axis whose
 * top is the raw series maximum is not one — the ticks come out at 31, 23.25,
 * 15.5 and nobody reads a bar against those. This rounds the top up to a step
 * off AXIS_STEPS so the labels land on numbers a person would have picked.
 *
 * A series that never exceeds the tick count gets one tick per whole number, so
 * a metric running at one or two a month is not labelled in halves. An all-zero
 * series still gets an axis, because a chart with no axis reads as broken while
 * a chart of zeros reads as zero.
 */

export function niceAxis(maxValue, tickCount = 4) {
  const max = Math.max(0, Number(maxValue) || 0);

  // An all-zero series still gets an axis, and a metric running at one or two a
  // month gets integer ticks rather than 0.5s.
  if (max <= tickCount) {
    return { max: tickCount, ticks: Array.from({ length: tickCount + 1 }, (_, i) => tickCount - i) };
  }

  const rough = max / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  const multiple = AXIS_STEPS.find((candidate) => candidate >= normalized) ?? 10;
  const step = multiple * magnitude;
  const top = step * tickCount;

  const ticks = [];
  for (let i = tickCount; i >= 0; i--) {
    // Rounded because a fractional step can accumulate float noise into a
    // label like "7.000000000000001".
    ticks.push(Math.round(step * i * 100) / 100);
  }
  return { max: top, ticks };
}

/** "Sep" for a month key, "W36" for an ISO week key. */
export function periodLabel(key) {
  if (key.includes("-W")) return `W${key.split("-W")[1]}`;
  const [year, month] = key.split("-");
  return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC",
  });
}
